import DashboardLayout from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { COUNTRIES_RO, formatInTz, TIMEZONES_RO } from "@/lib/format";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Archive, CheckCircle2, FolderKanban, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const STATUS_LABELS = { active: "Activ", on_hold: "În așteptare", finished: "Finalizat", archived: "Arhivat" };
const STATUS_VARIANTS = { active: "default", on_hold: "secondary", finished: "outline", archived: "destructive" };

export default function ProjectsPage() {
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const { data: projects, isLoading } = useQuery({
    queryKey: ["projects", showArchived],
    queryFn: () => api.projects.list({ includeArchived: showArchived }),
  });

  const inval = () => { qc.invalidateQueries({ queryKey: ["projects"] }); qc.invalidateQueries({ queryKey: ["stats"] }); };
  const createMut = useMutation({ mutationFn: api.projects.create, onSuccess: () => { inval(); setOpen(false); toast.success("Proiect creat"); }, onError: (e) => toast.error(apiError(e)) });
  const statusMut = useMutation({ mutationFn: ({ id, status }) => api.projects.update(id, { status }), onSuccess: () => { inval(); toast.success("Status actualizat"); }, onError: (e) => toast.error(apiError(e)) });
  const archiveMut = useMutation({ mutationFn: (id) => api.projects.archive(id), onSuccess: () => { inval(); toast.success("Proiect arhivat"); }, onError: (e) => toast.error(apiError(e)) });
  const deleteMut = useMutation({ mutationFn: (id) => api.projects.remove(id), onSuccess: () => { inval(); toast.success("Proiect șters"); }, onError: (e) => toast.error(apiError(e)) });

  const [form, setForm] = useState({ name: "", description: "", country: "RO", timezone: "Europe/Bucharest" });

  return (
    <DashboardLayout>
      <div className="flex flex-col gap-6" data-testid="projects-page">
        <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-semibold tracking-tight">Proiecte (șantiere)</h1>
            <p className="text-sm text-muted-foreground mt-1">Gestionați șantierele, fusul orar local și statusul lucrărilor.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm">
              <Switch checked={showArchived} onCheckedChange={setShowArchived} id="archived" data-testid="toggle-archived" />
              <Label htmlFor="archived" className="text-muted-foreground cursor-pointer">Arată arhivate</Label>
            </div>
            {isAdmin && (
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild><Button data-testid="new-project-btn"><Plus className="h-4 w-4 mr-1" />Proiect nou</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Creare șantier</DialogTitle>
                    <DialogDescription>Completați datele și fusul orar local. Toate datele/orele vor fi afișate în acest fus.</DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-3">
                    <div className="grid gap-1.5">
                      <Label>Nume șantier</Label>
                      <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Ex: Șantier Pipera" data-testid="project-name-input" />
                    </div>
                    <div className="grid gap-1.5">
                      <Label>Descriere (opțional)</Label>
                      <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Detalii despre amplasament, beneficiar etc." />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="grid gap-1.5">
                        <Label>Țară</Label>
                        <Select value={form.country} onValueChange={(v) => setForm((f) => ({ ...f, country: v }))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{COUNTRIES_RO.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-1.5">
                        <Label>Fus orar local</Label>
                        <Select value={form.timezone} onValueChange={(v) => setForm((f) => ({ ...f, timezone: v }))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{TIMEZONES_RO.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)} className="bg-background">Anulează</Button>
                    <Button data-testid="save-project-btn" onClick={() => {
                      if (!form.name.trim()) { toast.error("Numele este obligatoriu"); return; }
                      createMut.mutate({ name: form.name.trim(), description: form.description.trim() || undefined, country: form.country, timezone: form.timezone });
                    }} disabled={createMut.isPending}>{createMut.isPending ? "Se creează..." : "Creează"}</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </header>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-40" />)}</div>
        ) : !projects?.length ? (
          <Card><CardContent className="py-12 flex flex-col items-center text-center gap-3">
            <FolderKanban className="h-10 w-10 text-muted-foreground" />
            <p className="text-sm text-muted-foreground max-w-md">Nu există proiecte încă. Apăsați "Proiect nou" pentru a crea primul șantier.</p>
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((p) => (
              <Card key={p.id} className="flex flex-col" data-testid={`project-card-${p.id}`}>
                <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
                  <div className="min-w-0">
                    <CardTitle className="text-base truncate">{p.name}</CardTitle>
                    <p className="text-xs text-muted-foreground mt-1">{p.country} · {p.timezone}</p>
                  </div>
                  <Badge variant={STATUS_VARIANTS[p.status] ?? "default"}>{STATUS_LABELS[p.status] ?? p.status}</Badge>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col gap-3">
                  {p.description ? <p className="text-sm text-muted-foreground line-clamp-3">{p.description}</p> : <p className="text-sm text-muted-foreground italic">Fără descriere</p>}
                  <p className="text-xs text-muted-foreground mt-auto">Creat la {formatInTz(p.createdAt, p.timezone)}</p>
                  {isAdmin ? (
                    <div className="flex flex-wrap gap-2 pt-2 border-t">
                      {p.status !== "finished" && p.status !== "archived" && (
                        <Button size="sm" variant="outline" onClick={() => statusMut.mutate({ id: p.id, status: "finished" })} className="bg-background"><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Finalizează</Button>
                      )}
                      {p.status !== "archived" && (
                        <Button size="sm" variant="outline" onClick={() => archiveMut.mutate(p.id)} className="bg-background"><Archive className="h-3.5 w-3.5 mr-1" /> Arhivează</Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => { if (confirm(`Ștergi definitiv "${p.name}"?`)) deleteMut.mutate(p.id); }} className="bg-background text-destructive hover:text-destructive"><Trash2 className="h-3.5 w-3.5 mr-1" /> Șterge</Button>
                    </div>
                  ) : <p className="text-xs text-muted-foreground italic pt-2 border-t">Doar adminul poate modifica statusul.</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
